import type { QueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { AUDIT_HISTORY_LIMIT, auditHistoryEndpoints } from "@/api/audit/auditHistory";
import { permissionsEndpoints } from "@/api/roles-permissions/roles-permissions.endpoints";
import { userManagementEndpoints } from "./userManagement.endpoints";
import type { AuditHistoryResponse, AuditLog } from "@/api/audit/auditHistory.types";

export type { AuditLog };

export const USER_HISTORY_LIMIT = AUDIT_HISTORY_LIMIT;

/** Here rather than beside the hook, so the saves that change a person can refresh it. */
export const UserHistoryQueryKeys = {
  history: "userHistory",
  person: "userHistoryPerson",
  roleNames: "userHistoryRoleNames",
  roleRecord: "userHistoryRoleRecord",
  scopeBoxes: "userHistoryScopeBoxes",
} as const;

/**
 * The backend writes History through a queue after the save has returned (measured on dev: about
 * 0.3 s), so the re-read straight after a save can come back without it. A second one picks it up.
 */
const HISTORY_QUEUE_LAG_MS = 2000;

export const refreshUserHistory = (queryClient: QueryClient, queryKey: readonly unknown[]) => {
  void queryClient.invalidateQueries({ queryKey });
  setTimeout(() => void queryClient.invalidateQueries({ queryKey }), HISTORY_QUEUE_LAG_MS);
};

/**
 * Every change made to the person, in every company this login may see them in (SERV-3581): what
 * was saved through their record, filed under their id, and what `/access` filed under each row's
 * own id (temporary roles, adjustments, client assignments, scope rules), removed rows included.
 * The acting entity rides along from the request interceptor (the open record's company, else the
 * label-menu pick), and the backend answers within that scope: a picked company limits the
 * history to that company.
 */
export const getUserHistory = async (
  userId: string,
  signal?: AbortSignal,
): Promise<{ logs: AuditLog[]; total: number }> => {
  const { data } = await axiosInstance.get<AuditHistoryResponse>(
    auditHistoryEndpoints.personHistory(userId),
    {
      params: { limit: USER_HISTORY_LIMIT },
      signal,
      suppressErrorAlert: true,
    } as CustomAxiosRequestConfig,
  );
  return { logs: data.data?.logs ?? [], total: data.data?.total ?? 0 };
};

/*
 * The name lookups below fail quietly: a manager since removed, or a list this login cannot read,
 * leaves its rows saying "this screen doesn't show", and the tab says names are missing. The app's
 * error toast would otherwise fire on every visit to Details.
 */
const QUIET = { suppressErrorAlert: true } as CustomAxiosRequestConfig;

/** Measured on dev: 101 roles at ServEngine level. One page holds them all with room to spare. */
const ROLE_NAMES_PAGE_SIZE = 200;

export type RoleNames = {
  byId: Map<string, string>;
  /** Every company's copy of a role shares its code, so a code names a copy this list lacks. */
  byCode: Map<string, string>;
};

export const getRoleNames = async (signal?: AbortSignal): Promise<RoleNames> => {
  const { data } = await axiosInstance.get<{
    data?: { id: string; name: string; code?: string | null }[];
  }>(permissionsEndpoints.getAllRoles, {
    ...QUIET,
    params: { page: 1, pageSize: ROLE_NAMES_PAGE_SIZE },
    signal,
  });
  const roles = data.data ?? [];
  return {
    byId: new Map(roles.map((role) => [role.id, role.name])),
    byCode: new Map(roles.flatMap((role) => (role.code ? [[role.code, role.name] as const] : []))),
  };
};

/**
 * One role's own record, read in the company its History entry was filed in: a role in another
 * company's copy, or one the person no longer holds, is in no list this login reads. The backend
 * finds a role by id in the request's company only.
 */
export const getRoleRecordName = async (
  roleId: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<string> => {
  const { data } = await axiosInstance.get<{ data: { name: string } }>(
    `${permissionsEndpoints.getRoleById}/${roleId}`,
    { ...QUIET, actingEntityId: tenantId, signal } as CustomAxiosRequestConfig,
  );
  return data.data.name;
};

export const getScopeBoxNames = async (signal?: AbortSignal): Promise<Map<string, string>> => {
  const { data } = await axiosInstance.get<{
    data?: { id?: string | number; label: string }[];
  }>(userManagementEndpoints.exclusions, { ...QUIET, signal });
  return new Map(
    (data.data ?? [])
      .filter((item) => item.id !== undefined)
      .map((item) => [String(item.id), item.label]),
  );
};

export const getPersonName = async (userId: string, signal?: AbortSignal): Promise<string> => {
  const { data } = await axiosInstance.get<{ data: { firstName: string; lastName: string } }>(
    `${userManagementEndpoints.users}/${userId}`,
    { ...QUIET, signal },
  );
  return `${data.data.firstName} ${data.data.lastName}`.trim();
};
